<template>
  <Sidebar
    @open-create-modal="openCreateModal"
    @logout="logout"
  />

  <div class="lg:ml-64 p-4 md:p-8 w-full min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">Comprehensive Reports</h1>
        <p class="text-gray-600">Filter, analyze, and export cash requisition data</p>
      </div>

      <!-- Filters Section -->
      <div class="bg-white rounded-lg p-6 mb-6 border border-gray-200">
        <h2 class="text-xl font-semibold text-gray-900 mb-4">Filters</h2>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Date Range -->
          <div>
            <label class="block text-gray-700 text-sm mb-2">Start Date</label>
            <input
              v-model="filters.startDate"
              type="date"
              class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            />
          </div>

          <div>
            <label class="block text-gray-700 text-sm mb-2">End Date</label>
            <input
              v-model="filters.endDate"
              type="date"
              class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            />
          </div>

          <!-- Amount Range -->
          <div>
            <label class="block text-gray-700 text-sm mb-2">Min Amount (RF)</label>
            <input
              v-model.number="filters.minAmount"
              type="number"
              placeholder="e.g., 100000"
              class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            />
          </div>

          <div>
            <label class="block text-gray-700 text-sm mb-2">Max Amount (RF)</label>
            <input
              v-model.number="filters.maxAmount"
              type="number"
              placeholder="e.g., 500000"
              class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            />
          </div>

          <!-- Department -->
          <div>
            <label class="block text-gray-700 text-sm mb-2">Department</label>
            <select
              v-model="filters.department"
              class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            >
              <option value="">All Departments</option>
              <option v-for="dept in departments" :key="dept" :value="dept">
                {{ dept }}
              </option>
            </select>
          </div>

          <!-- Requesters (multi-select) -->
          <div class="relative" ref="requesterPicker">
            <label class="block text-gray-700 text-sm mb-2">Requesters</label>
            <button
              type="button"
              @click="requesterDropdownOpen = !requesterDropdownOpen"
              class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-left text-gray-900 focus:border-red-500 focus:outline-none flex justify-between items-center"
            >
              <span class="truncate">{{ requesterSummary }}</span>
              <svg class="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div
              v-if="requesterDropdownOpen"
              class="absolute z-20 mt-1 w-full min-w-[16rem] bg-white border border-gray-200 rounded-lg shadow-lg"
            >
              <div class="p-2 border-b border-gray-100">
                <input
                  v-model="requesterSearch"
                  type="text"
                  placeholder="Search people..."
                  class="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:border-red-500 focus:outline-none"
                />
              </div>
              <div class="max-h-60 overflow-y-auto py-1">
                <label
                  v-for="person in filteredRequesters"
                  :key="person._id"
                  class="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    :value="person._id"
                    v-model="filters.requester"
                    class="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span class="truncate">
                    {{ person.name }}
                    <span v-if="person.department" class="text-gray-500">({{ person.department }})</span>
                  </span>
                </label>
                <p v-if="filteredRequesters.length === 0" class="px-3 py-2 text-sm text-gray-500">No matches</p>
              </div>
              <div class="flex justify-between p-2 border-t border-gray-100 text-sm">
                <button type="button" @click="filters.requester = []" class="text-gray-600 hover:text-gray-900">
                  Clear
                </button>
                <button type="button" @click="requesterDropdownOpen = false; applyFilters()" class="text-red-600 hover:text-red-700 font-medium">
                  Apply
                </button>
              </div>
            </div>

            <!-- Selected people -->
            <div v-if="filters.requester.length" class="flex flex-wrap gap-1 mt-2">
              <span
                v-for="id in filters.requester"
                :key="id"
                class="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5 text-xs"
              >
                {{ requesterName(id) }}
                <button type="button" @click="removeRequester(id)" class="hover:text-red-900" title="Remove">&times;</button>
              </span>
            </div>
          </div>

          <!-- Status -->
          <div>
            <label class="block text-gray-700 text-sm mb-2">Status</label>
            <select
              v-model="filters.status"
              class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <!-- Priority -->
          <div>
            <label class="block text-gray-700 text-sm mb-2">Priority</label>
            <select
              v-model="filters.priority"
              class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            >
              <option value="">All Priorities</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>

          <!-- Settlement Status -->
          <div>
            <label class="block text-gray-700 text-sm mb-2">Settlement Status</label>
            <select
              v-model="filters.settlementStatus"
              class="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-red-500 focus:outline-none"
            >
              <option value="">All</option>
              <option value="pending_payment">Pending Payment</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <!-- Filter Actions -->
        <div class="flex gap-3 mt-6">
          <button
            @click="applyFilters"
            class="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
          >
            Apply Filters
          </button>
          <button
            @click="clearFilters"
            class="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>

      <!-- Export Buttons -->
      <div class="flex flex-wrap gap-3 mb-6">
        <button
          @click="exportReport('pdf')"
          :disabled="exporting"
          class="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {{ selectedCount ? `Export ${selectedCount} Selected (PDF)` : 'Export PDF' }}
        </button>

        <button
          @click="exportReport('excel')"
          :disabled="exporting"
          class="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          {{ selectedCount ? `Export ${selectedCount} Selected (Excel)` : 'Export Excel' }}
        </button>

        <button
          @click="viewAnalytics"
          class="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          View Analytics
        </button>
      </div>

      <!-- Selection Bar -->
      <div
        v-if="selectedCount > 0"
        class="flex flex-wrap items-center justify-between gap-3 mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm"
      >
        <span class="text-blue-800">
          <span class="font-semibold">{{ selectedCount }}</span> request{{ selectedCount === 1 ? '' : 's' }} selected
          <span class="text-blue-600">(selection is kept across pages and filters)</span>
        </span>
        <button @click="clearSelection" class="text-blue-700 hover:text-blue-900 font-medium underline">
          Clear selection
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
        <p class="text-gray-600 mt-4">Loading data...</p>
      </div>

      <!-- Results Table -->
      <div v-else-if="requests.length > 0" class="bg-white rounded-lg overflow-hidden border border-gray-200">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gray-50">
              <tr>
                <th class="pl-6 py-4 w-10">
                  <input
                    type="checkbox"
                    :checked="allOnPageSelected"
                    :indeterminate.prop="someOnPageSelected && !allOnPageSelected"
                    @change="toggleSelectPage($event.target.checked)"
                    class="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                    title="Select all on this page"
                  />
                </th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">Request ID</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">Requester</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">Department</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">Amount</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">Settlement</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr
                v-for="request in requests"
                :key="request._id"
                :class="selectedIds[request._id] ? 'bg-blue-50' : 'hover:bg-gray-50'"
                class="transition-colors"
              >
                <td class="pl-6 py-4">
                  <input
                    type="checkbox"
                    :checked="!!selectedIds[request._id]"
                    @change="toggleSelect(request._id, $event.target.checked)"
                    class="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
                  />
                </td>
                <td class="px-6 py-4 text-sm text-gray-600 font-mono">{{ request._id.slice(-8) }}</td>
                <td class="px-6 py-4 text-sm text-gray-900">
                  <button
                    v-if="request.requester"
                    @click="filterByRequester(request.requester)"
                    class="hover:text-red-600 hover:underline text-left"
                    :title="`Show only ${request.requester.name}'s requests`"
                  >
                    {{ request.requester.name }}
                  </button>
                  <span v-else>N/A</span>
                </td>
                <td class="px-6 py-4 text-sm text-gray-700">
                  <button
                    @click="filterByDepartment(request.department)"
                    class="hover:text-red-600 hover:underline text-left"
                    :title="`Show only ${request.department} requests`"
                  >
                    {{ request.department }}
                  </button>
                </td>
                <td class="px-6 py-4 text-sm text-gray-900 font-semibold">RF{{ request.amount.toLocaleString() }}</td>
                <td class="px-6 py-4 text-sm">
                  <span
                    :class="{
                      'bg-green-100 text-green-700': request.status === 'approved',
                      'bg-yellow-100 text-yellow-800': request.status === 'pending',
                      'bg-red-100 text-red-700': request.status === 'rejected'
                    }"
                    class="px-2 py-1 rounded-full text-xs font-medium"
                  >
                    {{ getStatusDisplay(request) }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm">
                  <span
                    :class="{
                      'bg-green-100 text-green-700': request.settlementStatus === 'paid',
                      'bg-yellow-100 text-yellow-700': request.settlementStatus === 'pending_payment',
                      'bg-gray-100 text-gray-700': request.settlementStatus === 'cancelled'
                    }"
                    class="px-2 py-1 rounded-full text-xs font-medium"
                  >
                    {{ request.settlementStatus || 'N/A' }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm text-gray-600">
                  {{ formatDateTime(request.createdAt) }}
                </td>
                <td class="px-6 py-4 text-sm">
                  <button
                    @click="viewRequestDetails(request)"
                    class="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                    View
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination" class="bg-gray-50 px-6 py-4 flex justify-between items-center border-t border-gray-200">
          <div class="text-sm text-gray-600">
            Showing {{ (pagination.currentPage - 1) * pagination.recordsPerPage + 1 }} to
            {{ Math.min(pagination.currentPage * pagination.recordsPerPage, pagination.totalRecords) }}
            of {{ pagination.totalRecords }} results
          </div>
          <div class="flex gap-2">
            <button
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="pagination.currentPage === 1"
              class="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="pagination.currentPage === pagination.totalPages"
              class="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <!-- No Results -->
      <div v-else class="bg-white rounded-lg p-8 text-center border border-gray-200">
        <p class="text-gray-600 text-lg">No requests found matching your filters</p>
      </div>

      <!-- Analytics Modal -->
      <div
        v-if="showAnalytics"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        @click.self="showAnalytics = false"
      >
        <div class="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <h2 class="text-2xl font-bold text-gray-900 mb-6">Analytics Dashboard</h2>

          <div v-if="analytics" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <!-- Status Breakdown -->
            <div class="bg-gray-50 rounded-lg p-4 border border-gray-200" v-if="analytics.statusBreakdown">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">By Status</h3>
              <div v-for="item in analytics.statusBreakdown" :key="item._id" class="mb-2">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">{{ item._id }}</span>
                  <span class="text-gray-900 font-semibold">{{ item.count }}</span>
                </div>
                <div class="text-xs text-gray-500">RF{{ item.totalAmount.toLocaleString() }}</div>
              </div>
            </div>

            <!-- Department Breakdown -->
            <div class="bg-gray-50 rounded-lg p-4 border border-gray-200" v-if="analytics.departmentBreakdown">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">By Department</h3>
              <div v-for="item in analytics.departmentBreakdown" :key="item._id" class="mb-2">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">{{ item._id }}</span>
                  <span class="text-gray-900 font-semibold">{{ item.count }}</span>
                </div>
                <div class="text-xs text-gray-500">RF{{ item.totalAmount.toLocaleString() }}</div>
              </div>
            </div>

            <!-- Priority Breakdown -->
            <div class="bg-gray-50 rounded-lg p-4 border border-gray-200" v-if="analytics.priorityBreakdown">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">By Priority</h3>
              <div v-for="item in analytics.priorityBreakdown" :key="item._id" class="mb-2">
                <div class="flex justify-between text-sm">
                  <span class="text-gray-600">{{ item._id }}</span>
                  <span class="text-gray-900 font-semibold">{{ item.count }}</span>
                </div>
                <div class="text-xs text-gray-500">RF{{ item.totalAmount.toLocaleString() }}</div>
              </div>
            </div>

            <!-- Total Stats -->
            <div class="bg-gray-50 rounded-lg p-4 border border-gray-200 md:col-span-2 lg:col-span-3" v-if="analytics.totalStats">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Total Statistics</h3>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p class="text-sm text-gray-600">Total Requested</p>
                  <p class="text-xl font-bold text-gray-900">RF{{ analytics.totalStats.totalRequested?.toLocaleString() || 0 }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-600">Total Approved</p>
                  <p class="text-xl font-bold text-green-600">RF{{ analytics.totalStats.totalApproved?.toLocaleString() || 0 }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-600">Total Rejected</p>
                  <p class="text-xl font-bold text-red-600">RF{{ analytics.totalStats.totalRejected?.toLocaleString() || 0 }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-600">Total Pending</p>
                  <p class="text-xl font-bold text-yellow-600">RF{{ analytics.totalStats.totalPending?.toLocaleString() || 0 }}</p>
                </div>
              </div>
            </div>
          </div>

          <button
            @click="showAnalytics = false"
            class="mt-6 w-full bg-gray-100 hover:bg-gray-200 text-gray-900 px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      <!-- Request Details Modal -->
      <div
        v-if="showDetailsModal && selectedRequest"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        @click.self="closeDetailsModal"
      >
        <div class="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-2xl font-bold text-gray-900">Request Details</h2>
            <button @click="closeDetailsModal" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <div class="space-y-6">
            <!-- Request Header -->
            <div class="bg-gray-50 rounded-lg p-5 border border-gray-200">
              <div class="flex flex-wrap justify-between items-start gap-4 mb-4">
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-2">
                    Request #{{ selectedRequest._id.slice(-8).toUpperCase() }}
                  </h3>
                  <div class="flex flex-wrap gap-2">
                    <span
                      :class="{
                        'bg-red-100 text-red-700': selectedRequest.priority === 'High',
                        'bg-yellow-100 text-yellow-700': selectedRequest.priority === 'Medium',
                        'bg-green-100 text-green-700': selectedRequest.priority === 'Low'
                      }"
                      class="px-2 py-1 rounded-full text-xs font-medium"
                    >
                      {{ selectedRequest.priority || 'Medium' }} Priority
                    </span>
                    <span
                      :class="{
                        'bg-green-100 text-green-700': selectedRequest.status === 'approved',
                        'bg-yellow-100 text-yellow-800': selectedRequest.status === 'pending',
                        'bg-red-100 text-red-700': selectedRequest.status === 'rejected'
                      }"
                      class="px-2 py-1 rounded-full text-xs font-medium"
                    >
                      {{ getStatusDisplay(selectedRequest) }}
                    </span>
                  </div>
                </div>
                <div class="text-right">
                  <p class="text-3xl font-bold text-gray-900">RF {{ selectedRequest.amount.toLocaleString() }}</p>
                  <p class="text-sm text-gray-600 mt-1">{{ selectedRequest.paymentMethod || 'Bank Transfer' }}</p>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p class="text-gray-500 mb-1">Requester</p>
                  <p class="font-semibold text-gray-900">{{ selectedRequest.requester?.name || 'N/A' }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Department</p>
                  <p class="font-semibold text-gray-900">{{ selectedRequest.department || 'N/A' }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Beneficiary</p>
                  <p class="font-semibold text-gray-900">{{ selectedRequest.supplier || 'N/A' }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Beneficiary Account/MoMo</p>
                  <p class="font-semibold text-gray-900">{{ selectedRequest.beneficiaryAccount || 'N/A' }}</p>
                </div>
                <div>
                  <p class="text-gray-500 mb-1">Preferred Payment Date</p>
                  <p class="font-semibold text-gray-900">{{ formatDateTime(selectedRequest.preferredPaymentDate) }}</p>
                </div>
                <div class="md:col-span-2">
                  <p class="text-gray-500 mb-1">Created At</p>
                  <p class="font-semibold text-gray-900">{{ formatDateTime(selectedRequest.createdAt) }}</p>
                </div>
              </div>
            </div>

            <!-- Purpose -->
            <div>
              <h4 class="text-sm font-semibold text-gray-700 mb-2">Purpose</h4>
              <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <p class="text-gray-900">{{ selectedRequest.purpose }}</p>
              </div>
            </div>

            <!-- Approval Path -->
            <div>
              <h4 class="text-sm font-semibold text-gray-700 mb-3">Approval Path</h4>
              <ApprovalPathVisualization
                :amount="selectedRequest.amount"
                :approvalPath="selectedRequest.approvalPath"
                :status="selectedRequest.status"
              />
            </div>

            <!-- Attachments -->
            <div v-if="hasAttachments(selectedRequest)">
              <h4 class="text-sm font-semibold text-gray-700 mb-2">Attachments</h4>
              <div class="flex flex-wrap gap-2">
                <button
                  v-if="selectedRequest.attachments?.invoice"
                  @click="downloadAttachment(selectedRequest._id, 'invoice')"
                  class="flex items-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  Invoice
                </button>
                <button
                  v-if="selectedRequest.attachments?.contract"
                  @click="downloadAttachment(selectedRequest._id, 'contract')"
                  class="flex items-center gap-2 bg-green-100 hover:bg-green-200 text-green-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  Contract
                </button>
                <button
                  v-for="(doc, index) in selectedRequest.attachments?.otherDocuments || []"
                  :key="index"
                  @click="downloadAttachment(selectedRequest._id, `other-${index}`)"
                  class="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/>
                  </svg>
                  Document {{ index + 1 }}
                </button>
              </div>
            </div>

            <!-- Finance Review -->
            <div v-if="selectedRequest.financeReview && selectedRequest.financeReview.comments">
              <h4 class="text-sm font-semibold text-gray-700 mb-2">Finance Review</h4>
              <div class="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 text-sm">
                  <div>
                    <span class="text-blue-600 font-medium">Treasury Status:</span>
                    <span class="text-blue-800 ml-1">{{ selectedRequest.financeReview.treasuryStatus || 'N/A' }}</span>
                  </div>
                  <div>
                    <span class="text-blue-600 font-medium">Budget Available:</span>
                    <span class="text-blue-800 ml-1">{{ selectedRequest.financeReview.budgetAvailable || 'N/A' }}</span>
                  </div>
                  <div>
                    <span class="text-blue-600 font-medium">Cashflow Impact:</span>
                    <span class="text-blue-800 ml-1">{{ selectedRequest.financeReview.cashflowImpact || 'N/A' }}</span>
                  </div>
                </div>
                <p class="text-blue-800 text-sm">{{ selectedRequest.financeReview.comments }}</p>
                <div class="text-xs text-blue-600 mt-2">
                  Reviewed by: {{ selectedRequest.financeReview.reviewedBy?.name || 'N/A' }}
                  <span v-if="selectedRequest.financeReview.reviewedAt"> - {{ formatDateTime(selectedRequest.financeReview.reviewedAt) }}</span>
                </div>
              </div>
            </div>

            <!-- MD Review -->
            <div v-if="selectedRequest.mdReview && selectedRequest.mdReview.comments">
              <h4 class="text-sm font-semibold text-gray-700 mb-2">Managing Director Review</h4>
              <div class="bg-purple-50 border-l-4 border-purple-400 p-4 rounded-r">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 text-sm">
                  <div>
                    <span class="text-purple-600 font-medium">Operational Need Confirmed:</span>
                    <span class="text-purple-800 ml-1">{{ selectedRequest.mdReview.operationalNeedConfirmed || 'N/A' }}</span>
                  </div>
                  <div>
                    <span class="text-purple-600 font-medium">Approved Amount:</span>
                    <span class="text-purple-800 ml-1">RF {{ (selectedRequest.mdReview.approvedAmount || 0).toLocaleString() }}</span>
                  </div>
                </div>
                <p class="text-purple-800 text-sm">{{ selectedRequest.mdReview.comments }}</p>
                <div class="text-xs text-purple-600 mt-2">
                  Reviewed by: {{ selectedRequest.mdReview.reviewedBy?.name || 'N/A' }}
                  <span v-if="selectedRequest.mdReview.reviewedAt"> - {{ formatDateTime(selectedRequest.mdReview.reviewedAt) }}</span>
                </div>
              </div>
            </div>

            <!-- Settlement Information -->
            <div v-if="selectedRequest.status === 'approved'">
              <h4 class="text-sm font-semibold text-gray-700 mb-2">Settlement Information</h4>
              <div class="bg-green-50 border-l-4 border-green-400 p-4 rounded-r">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span class="text-green-600 font-medium">Settlement Status:</span>
                    <span class="text-green-800 ml-1">{{ selectedRequest.settlementStatus || 'Pending Payment' }}</span>
                  </div>
                  <div v-if="selectedRequest.paymentReference">
                    <span class="text-green-600 font-medium">Payment Reference:</span>
                    <span class="text-green-800 ml-1">{{ selectedRequest.paymentReference }}</span>
                  </div>
                  <div v-if="selectedRequest.paymentDate">
                    <span class="text-green-600 font-medium">Payment Date:</span>
                    <span class="text-green-800 ml-1">{{ formatDateTime(selectedRequest.paymentDate) }}</span>
                  </div>
                  <div v-if="selectedRequest.settledBy">
                    <span class="text-green-600 font-medium">Settled By:</span>
                    <span class="text-green-800 ml-1">{{ selectedRequest.settledBy?.name || 'N/A' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-6 flex justify-end">
            <button
              @click="closeDetailsModal"
              class="bg-gray-100 hover:bg-gray-200 text-gray-900 px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';
import ApprovalPathVisualization from './ApprovalPathVisualization.vue';

export default {
  name: 'Reports',
  components: { Sidebar, ApprovalPathVisualization },
  data() {
    return {
      filters: {
        startDate: '',
        endDate: '',
        minAmount: null,
        maxAmount: null,
        department: '',
        requester: [],
        status: '',
        priority: '',
        settlementStatus: ''
      },
      departments: ['Sales', 'Technical', 'Transport', 'Finance & Logistics', 'Management', 'Software Development', 'IT Support'],
      requesters: [],
      requesterSearch: '',
      requesterDropdownOpen: false,
      selectedIds: {},
      user: JSON.parse(localStorage.getItem('user') || '{}'),
      requests: [],
      pagination: null,
      loading: false,
      exporting: false,
      showAnalytics: false,
      analytics: null,
      currentPage: 1,
      showDetailsModal: false,
      selectedRequest: null
    };
  },
  computed: {
    filteredRequesters() {
      const term = this.requesterSearch.trim().toLowerCase();
      if (!term) return this.requesters;
      return this.requesters.filter(p =>
        (p.name || '').toLowerCase().includes(term) ||
        (p.department || '').toLowerCase().includes(term)
      );
    },
    requesterSummary() {
      const count = this.filters.requester.length;
      if (count === 0) return 'All Requesters';
      if (count === 1) return this.requesterName(this.filters.requester[0]);
      return `${count} people selected`;
    },
    selectedCount() {
      return Object.keys(this.selectedIds).length;
    },
    allOnPageSelected() {
      return this.requests.length > 0 && this.requests.every(r => this.selectedIds[r._id]);
    },
    someOnPageSelected() {
      return this.requests.some(r => this.selectedIds[r._id]);
    }
  },
  mounted() {
    this.fetchFilterOptions();
    this.fetchReports();
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    // Active filters as a plain object, without empty values
    activeFilters() {
      const result = {};
      Object.entries(this.filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length) result[key] = value.join(',');
        } else if (value !== '' && value !== null && value !== undefined) {
          result[key] = value;
        }
      });
      return result;
    },

    async fetchFilterOptions() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          'https://guarantee.iwacufinance.com/cash-request/api/reports/filter-options',
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        this.requesters = response.data.requesters || [];
        // Keep the known departments and add any others that appear in the data
        const merged = new Set([...this.departments, ...(response.data.departments || [])]);
        this.departments = [...merged].sort();
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    },

    async fetchReports() {
      this.loading = true;
      try {
        const token = localStorage.getItem('token');

        // Build query params
        const params = new URLSearchParams(this.activeFilters());
        params.append('page', this.currentPage);
        params.append('limit', 50);

        const response = await axios.get(
          `https://guarantee.iwacufinance.com/cash-request/api/reports/requests?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        this.requests = response.data.requests;
        this.pagination = response.data.pagination;
      } catch (error) {
        console.error('Error fetching reports:', error);
        alert('Failed to fetch reports');
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      this.currentPage = 1;
      this.fetchReports();
    },

    clearFilters() {
      this.filters = {
        startDate: '',
        endDate: '',
        minAmount: null,
        maxAmount: null,
        department: '',
        requester: [],
        status: '',
        priority: '',
        settlementStatus: ''
      };
      this.currentPage = 1;
      this.fetchReports();
    },

    goToPage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.currentPage = page;
        this.fetchReports();
      }
    },

    requesterName(id) {
      const person = this.requesters.find(p => p._id === id);
      return person ? person.name : 'Unknown';
    },

    removeRequester(id) {
      this.filters.requester = this.filters.requester.filter(r => r !== id);
      this.applyFilters();
    },

    handleOutsideClick(event) {
      const picker = this.$refs.requesterPicker;
      if (this.requesterDropdownOpen && picker && !picker.contains(event.target)) {
        this.requesterDropdownOpen = false;
      }
    },

    // Adds the person to the requester filter (keeps anyone already chosen)
    filterByRequester(requester) {
      if (!this.filters.requester.includes(requester._id)) {
        this.filters.requester = [...this.filters.requester, requester._id];
      }
      // Make sure the person is in the dropdown even if filter options failed to load
      if (!this.requesters.some(p => p._id === requester._id)) {
        this.requesters.push({ _id: requester._id, name: requester.name, department: requester.department });
      }
      this.applyFilters();
    },

    filterByDepartment(department) {
      this.filters.department = department;
      if (!this.departments.includes(department)) this.departments.push(department);
      this.applyFilters();
    },

    toggleSelect(id, checked) {
      const next = { ...this.selectedIds };
      if (checked) next[id] = true;
      else delete next[id];
      this.selectedIds = next;
    },

    toggleSelectPage(checked) {
      const next = { ...this.selectedIds };
      this.requests.forEach(r => {
        if (checked) next[r._id] = true;
        else delete next[r._id];
      });
      this.selectedIds = next;
    },

    clearSelection() {
      this.selectedIds = {};
    },

    // Exports the selected requests if any are selected, otherwise everything matching the filters
    async exportReport(format) {
      this.exporting = true;
      try {
        const token = localStorage.getItem('token');
        const body = this.selectedCount > 0
          ? { ids: Object.keys(this.selectedIds) }
          : this.activeFilters();

        const response = await axios.post(
          `https://guarantee.iwacufinance.com/cash-request/api/reports/export/${format}`,
          body,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );

        const extension = format === 'pdf' ? 'pdf' : 'xlsx';
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `cash-requests-report-${Date.now()}.${extension}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error(`Error exporting ${format}:`, error);
        // Error bodies come back as a Blob because of responseType: 'blob'
        let message = `Failed to export ${format === 'pdf' ? 'PDF' : 'Excel'}`;
        if (error.response?.data instanceof Blob) {
          try {
            message = JSON.parse(await error.response.data.text()).message || message;
          } catch (e) { /* not JSON */ }
        }
        alert(message);
      } finally {
        this.exporting = false;
      }
    },

    async viewAnalytics() {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (this.filters.startDate) params.append('startDate', this.filters.startDate);
        if (this.filters.endDate) params.append('endDate', this.filters.endDate);

        const response = await axios.get(
          `https://guarantee.iwacufinance.com/cash-request/api/reports/analytics?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        this.analytics = response.data;
        this.showAnalytics = true;
      } catch (error) {
        console.error('Error fetching analytics:', error);
        alert('Failed to fetch analytics');
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      // Format date with time in CAT (Central Africa Time, GMT+2)
      return new Date(dateString).toLocaleString('en-RW', {
        timeZone: 'Africa/Kigali',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }) + ' CAT';
    },
    getCurrentPendingApprover(request) {
      // Find the current pending approver from the approval path
      if (request.status === 'pending' && request.approvalPath && request.approvalPath.length > 0) {
        const pendingApproval = request.approvalPath.find(ap => ap.status === 'pending');
        if (pendingApproval && pendingApproval.approver) {
          return pendingApproval.approver.name || 'Unknown';
        }
      }
      return null;
    },
    getStatusDisplay(request) {
      if (request.status === 'pending') {
        const approver = this.getCurrentPendingApprover(request);
        return approver ? `Pending: ${approver}` : 'Pending';
      }
      return request.status.charAt(0).toUpperCase() + request.status.slice(1);
    },
    viewRequestDetails(request) {
      this.selectedRequest = request;
      this.showDetailsModal = true;
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedRequest = null;
    },
    hasAttachments(request) {
      return request.attachments && (
        request.attachments.invoice ||
        request.attachments.contract ||
        (request.attachments.otherDocuments && request.attachments.otherDocuments.length > 0)
      );
    },
    async downloadAttachment(requestId, type) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `https://guarantee.iwacufinance.com/cash-request/api/requests/attachment/${requestId}/${type}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${type}-${requestId}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        alert('Failed to download attachment');
      }
    },
    openCreateModal() {
      // Handle opening create modal
      console.log('Opening create modal');
    },
    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/');
    }
  }
};
</script>

<style scoped>
</style>
